import * as R from 'ramda';
import {
  all,
  call,
  delay,
  put,
  race,
  select,
  spawn,
  take,
  takeEvery,
  takeLeading,
  takeLatest,
} from 'redux-saga/effects';
import {
  account as accountApis,
  generativeAi as generativeAiApis,
  matcher as matcherApis,
  partnerManagement as partnerApis,
  ti2 as ti2Apis,
} from '../../../api';
import { apiSaga } from '../../../root-sagas';
import {
  SUPPORTED_JOB_STATUSES,
  TERMINAL_JOB_STATUSES,
} from '../Settings/Gondola/gondolaJobStatus';
import { integrationMetadata } from '../Settings/integrations/metadata';

const skipLoader = { skipLoader: true };

const openSnackbar = (snackbarMessage, snackBarSeverity = 'success') => ({
  type: 'global_openSnackBar',
  payload: { snackbarMessage, snackBarSeverity },
});

export function* getIntegrationContext() {
  let account = yield select(state => state.account);
  while (!R.path(['company', 'companyId'], account)) {
    yield take('@app/GET_ACCOUNT_INFO_SUCCESS');
    account = yield select(state => state.account);
  }

  const companyId = R.path(['company', 'companyId'], account);
  let ti2token = R.path(['travelMLSettings', 'ti2', 'token'], account);
  if (!ti2token) {
    ti2token = yield select(
      state => state.generativeAi.integrationSettings.ti2token
    );
  }
  if (!ti2token) {
    const { response, error } = yield call(ti2Apis.createToken);
    if (error || !response || !response.ti2token) {
      yield put(openSnackbar('Unable to initialize integration settings.', 'error'));
      return {};
    }
    ti2token = response.ti2token;
    yield put({ type: 'genAi_integrationSettings_setTi2Token', payload: ti2token });
  }
  return { companyId, ti2token };
}

export function* loadIntegrations() {
  const { companyId, ti2token } = yield call(getIntegrationContext);
  if (!companyId || !ti2token) return;
  const response = yield* apiSaga(
    'genAi_integrationSettings_loadIntegrations',
    ti2Apis.getAllAppKeys,
    [{ companyId, ti2token }, skipLoader]
  );
  if (!response) return;

  const integrations = response.userAppKeys || [];
  const integrationIds = R.uniq(integrations.map(R.prop('integrationId')).filter(Boolean));
  yield all(
    integrationIds.map(integrationId =>
      apiSaga(
        'genAi_integrationSettings_loadCompanySettings',
        ti2Apis.getCompanyIntegrationSettings,
        [{ companyId, integrationId, ti2token }, skipLoader]
      )
    )
  );

  const partners = yield select(
    state => state.PM.partnerList.list.partners
  );
  if (!partners.length) yield put({ type: '@partner-list/GET_PARTNERS' });
  const isAnAdmin = yield select(state => Boolean(state.account.isAnAdmin));
  if (isAnAdmin) {
    yield call(restoreTourplanRefreshSessions, companyId, ti2token, integrations);
    yield call(loadProductSyncInstances);
    yield call(loadProductFilesRuns);
  }
}

export function* loadProductSyncInstances() {
  yield* apiSaga(
    'genAi_productSync_instances',
    generativeAiApis.productSyncGetInstances,
    [skipLoader]
  );
}

const productSyncOwnerSelector = state => ({
  companyId: R.pathOr('', ['account', 'company', 'companyId'], state),
  instanceKey: R.pathOr('', [
    'generativeAi',
    'integrationSettings',
    'productSync',
    'detailInstanceKey',
  ], state),
});

const ownsProductSyncPage = (owner, companyId, instanceKey) => (
  owner.companyId === companyId && owner.instanceKey === instanceKey
);

export function* loadProductSyncDetail({ payload }) {
  const { companyId, instanceKey } = payload || {};
  if (!companyId || !instanceKey) return null;
  const owner = yield select(productSyncOwnerSelector);
  if (!ownsProductSyncPage(owner, companyId, instanceKey)) return null;
  return yield* apiSaga(
    'genAi_productSync_detail',
    generativeAiApis.productSyncGetInstance,
    [instanceKey, { companyId, instanceKey, skipLoader: true }]
  );
}

export function* enterProductSyncInstance({ payload }) {
  const { companyId, instanceKey } = payload || {};
  if (!companyId || !instanceKey) return;
  const detail = yield call(loadProductSyncDetail, { payload });
  const owner = yield select(productSyncOwnerSelector);
  if (!detail || !ownsProductSyncPage(owner, companyId, instanceKey)) return;
  const activeRun = detail.activeRun || {};
  if (!activeRun.runId) return;
  yield put({
    type: 'genAi_productSync_pollRun',
    payload: {
      companyId,
      instanceKey,
      runId: activeRun.runId,
      action: activeRun.action,
    },
  });
}

export function* pollProductSyncJob({ companyId, instanceKey, runId }) {
  if (!companyId || !instanceKey || !runId) return;
  while (true) {
    let owner = yield select(productSyncOwnerSelector);
    if (!ownsProductSyncPage(owner, companyId, instanceKey)) return;

    let jobResp = null;
    try {
      const { response } = yield call(matcherApis.checkStatus, {
        jobs: [{ jobId: runId, jobType: 'pyFileMatch' }],
        skipLoader: true,
      });
      jobResp = Array.isArray(response) ? response[0] : null;
    } catch (_) {
      yield delay(10e3);
      continue;
    }
    if (!jobResp) {
      yield delay(10e3);
      continue;
    }

    owner = yield select(productSyncOwnerSelector);
    if (!ownsProductSyncPage(owner, companyId, instanceKey)) return;
    yield put({
      type: 'genAi_productSync_jobProgress',
      payload: { companyId, instanceKey, runId, jobResp },
    });

    const status = typeof jobResp.status === 'string' ? jobResp.status.toLowerCase() : '';
    const unsupportedStatus = Boolean(status && !SUPPORTED_JOB_STATUSES.has(status));
    if (jobResp.isTerminal === true || TERMINAL_JOB_STATUSES.has(status) || unsupportedStatus) {
      yield call(loadProductSyncDetail, { payload: { companyId, instanceKey } });
      owner = yield select(productSyncOwnerSelector);
      if (ownsProductSyncPage(owner, companyId, instanceKey)) {
        yield call(loadProductSyncInstances);
        yield call(loadProductFilesRuns);
        yield put({
          type: 'genAi_productSync_pollingStopped',
          payload: { companyId, instanceKey, runId },
        });
      }
      return;
    }
    yield delay(10e3);
  }
}

export function* handleProductSyncPolling({ payload }) {
  yield put({ type: 'genAi_productSync_pollingStarted', payload });
  yield race({
    poll: call(pollProductSyncJob, payload),
    cancel: take([
      'genAi_productSync_enterInstance',
      'genAi_productSync_leaveInstance',
    ]),
  });
}

export function* startProductSyncRun({ payload }) {
  const { action, companyId, dataHash, instanceKey } = payload || {};
  if (!action || !companyId || !instanceKey) return;
  let owner = yield select(productSyncOwnerSelector);
  if (!ownsProductSyncPage(owner, companyId, instanceKey)) return;
  const request = {
    action,
    ...(action === 'populate_snapshot' ? { dataHash } : {}),
  };
  const result = yield* apiSaga(
    'genAi_productSync_startRun',
    generativeAiApis.productSyncStartRun,
    [instanceKey, request, { companyId, instanceKey, skipLoader: true }]
  );
  const run = result && result.run;
  if (!run || !run.runId) return;
  owner = yield select(productSyncOwnerSelector);
  if (!ownsProductSyncPage(owner, companyId, instanceKey)) return;
  yield put({
    type: 'genAi_productSync_pollRun',
    payload: {
      companyId,
      instanceKey,
      runId: run.runId,
      action,
    },
  });
}

export function* updateProductSyncAutoSync({ payload }) {
  const { companyId, instanceKey, paused } = payload || {};
  if (!companyId || !instanceKey || typeof paused !== 'boolean') return;
  let owner = yield select(productSyncOwnerSelector);
  if (!ownsProductSyncPage(owner, companyId, instanceKey)) return;
  const result = yield* apiSaga(
    'genAi_productSync_autoSync',
    generativeAiApis.productSyncUpdateAutoSync,
    [instanceKey, { paused }, { companyId, instanceKey, skipLoader: true }]
  );
  if (!result) return;
  owner = yield select(productSyncOwnerSelector);
  if (!ownsProductSyncPage(owner, companyId, instanceKey)) return;
  yield call(loadProductSyncDetail, { payload: { companyId, instanceKey } });
  owner = yield select(productSyncOwnerSelector);
  if (!ownsProductSyncPage(owner, companyId, instanceKey)) return;
  yield call(loadProductSyncInstances);
  yield put(openSnackbar(paused ? 'Automatic sync paused.' : 'Automatic sync resumed.'));
}

export function* inspectProductSyncChroma({ payload }) {
  const { companyId, instanceKey } = payload || {};
  if (!companyId || !instanceKey) return;
  const owner = yield select(productSyncOwnerSelector);
  if (!ownsProductSyncPage(owner, companyId, instanceKey)) return;
  yield* apiSaga(
    'genAi_productSync_inspection',
    generativeAiApis.productSyncInspectChroma,
    [instanceKey, { companyId, instanceKey, skipLoader: true }]
  );
}

export function* loadIntegrationData({ payload = {} }) {
  const { integrationId, hint } = payload;
  if (!integrationId) return;
  const { companyId, ti2token } = yield call(getIntegrationContext);
  if (!companyId || !ti2token) return;
  const templateLoaded = yield select(
    state => R.pathOr(false, [
      'generativeAi',
      'integrationSettings',
      'templates',
      integrationId,
      'loaded',
    ], state)
  );
  const requests = [];
  if (!templateLoaded) {
    requests.push(
      apiSaga(
        'genAi_integrationSettings_loadTemplate',
        ti2Apis.getIntegrationTemplate,
        [{ integrationId, ti2token }, skipLoader]
      )
    );
  }
  requests.push(
    apiSaga(
      'genAi_integrationSettings_loadCompanySettings',
      ti2Apis.getCompanyIntegrationSettings,
      [{ companyId, integrationId, ti2token }, skipLoader]
    )
  );
  if (hint) {
    requests.push(
      apiSaga(
        'genAi_integrationSettings_loadToken',
        ti2Apis.loadIntegrationToken,
        [{ companyId, integrationId, hint, ti2token }, skipLoader]
      )
    );
  }
  yield all(requests);
}

function* runValidation(integration, showResult = true) {
  const { companyId, ti2token } = yield call(getIntegrationContext);
  if (!companyId || !ti2token) return;
  const result = yield* apiSaga(
    'genAi_integrationSettings_validate',
    ti2Apis.validateIntegration,
    [{ ...integration, companyId, ti2token }, skipLoader]
  );
  if (showResult && result) {
    yield put(
      openSnackbar(
        result.valid ? 'The integration is valid.' : 'The integration is not valid.',
        result.valid ? 'success' : 'warning'
      )
    );
  }
  return result;
}

export function* validateIntegration({ payload }) {
  yield call(runValidation, payload, true);
}

export const defaultScopeAssignmentSucceeded = result => {
  const response = result && result.response;
  if (!response || response.skipped) return false;
  if (response.added === true) return true;
  const ti2s = response.ti2s || [];
  return response.added === false && ti2s.some(
    item => item.app === result.integrationId && item.hint === (result.hint || null)
  );
};

export const defaultScopeAssignmentMessage = result => {
  const response = result && result.response;
  if (!response) return 'Integration saved, but it could not be assigned to the Default scope.';
  if (response.skipped === 'scope_not_empty') {
    return 'Integration saved, but the Default scope already has an integration.';
  }
  return defaultScopeAssignmentSucceeded(result)
    ? 'Integration saved, validated, and assigned to the Default scope.'
    : 'Integration saved, but it could not be assigned to the Default scope.';
};

export function* saveIntegration({ payload }) {
  const { companyId, ti2token } = yield call(getIntegrationContext);
  if (!companyId || !ti2token) return;
  const existingIntegrations = yield select(
    state => state.generativeAi.integrationSettings.integrations
  );
  const metadata = integrationMetadata[payload.integrationId] || {};
  let hint = payload.hint;

  if (payload.newPartner && payload.newPartner.name) {
    const email = String(payload.newPartner.email || '').trim();
    const createdPartner = yield* apiSaga(
      'genAi_integrationSettings_createPartner',
      partnerApis.createOnePartner,
      [{
        name: payload.newPartner.name,
        contacts: email ? [{ email }] : [],
        origin: 'bookingsai-affiliate',
      }, skipLoader]
    );
    if (!R.path(['partner', 'id'], createdPartner)) return;
    hint = createdPartner.partner.id;
  }

  const token = payload.token || {};
  const companyAttributes = metadata.companyAttributes || [];
  const companySettings = R.pick(companyAttributes, token);
  const connectionToken = R.omit(companyAttributes, token);
  const integration = { integrationId: payload.integrationId, hint };

  const companySettingsResult = yield* apiSaga(
    'genAi_integrationSettings_saveCompanySettings',
    ti2Apis.saveCompanyIntegrationSettings,
    [{ ...integration, companyId, ti2token, settings: companySettings }, skipLoader]
  );
  if (!companySettingsResult) return;

  const savedIntegration = yield* apiSaga(
    'genAi_integrationSettings_save',
    ti2Apis.saveIntegration,
    [{
      ...integration,
      companyId,
      ti2token,
      token: JSON.stringify(connectionToken),
    }, skipLoader]
  );
  if (!savedIntegration) return;

  yield put({ type: 'genAi_integrationSettings_closeEditor' });
  yield call(loadIntegrations);
  const validationResult = yield call(runValidation, integration, false);
  if (!validationResult || !validationResult.valid) {
    yield put(openSnackbar('Integration saved, but validation failed.', 'warning'));
    return;
  }

  const wasFirstForIntegration = !existingIntegrations.some(
    item => item.integrationId === integration.integrationId
  );
  if (metadata.autoCreateAffiliates && wasFirstForIntegration) {
    yield* apiSaga(
      'genAi_integrationSettings_createAffiliates',
      accountApis.createAffiliates,
      [integration, skipLoader]
    );
  }

  if (savedIntegration.isFirstIntegration === true) {
    const assignmentResult = yield call(
      generativeAiApis.gondolaSeedDefaultScopeTi2,
      integration
    );
    yield put(
      openSnackbar(
        defaultScopeAssignmentMessage({
          ...assignmentResult,
          ...integration,
        }),
        defaultScopeAssignmentSucceeded({ ...assignmentResult, ...integration })
          ? 'success' : 'warning'
      )
    );
    return;
  }

  const message = typeof savedIntegration.isFirstIntegration === 'boolean'
    ? 'Integration saved and validated.'
    : 'Integration saved and validated, but Default scope assignment requires the latest integration service.';
  yield put(openSnackbar(
    message,
    typeof savedIntegration.isFirstIntegration === 'boolean' ? 'success' : 'warning'
  ));
}

export function* saveCompanySettings({ payload }) {
  const { companyId, ti2token } = yield call(getIntegrationContext);
  if (!companyId || !ti2token) return;
  const result = yield* apiSaga(
    'genAi_integrationSettings_saveCompanySettings',
    ti2Apis.saveCompanyIntegrationSettings,
    [{ ...payload, companyId, ti2token }, skipLoader]
  );
  if (!result) return;
  yield put({ type: 'genAi_integrationSettings_closeCompanyDialog' });
  yield put(openSnackbar('Company settings saved.'));
  yield call(loadIntegrationData, { payload });
}

export function* removeIntegration({ payload }) {
  const result = yield* apiSaga(
    'genAi_integrationSettings_remove',
    generativeAiApis.removeIntegration,
    [payload, skipLoader]
  );
  if (!result) {
    yield put({ type: 'genAi_integrationSettings_closeRemoveDialog' });
    yield call(loadIntegrations);
    return;
  }
  yield put({ type: 'genAi_integrationSettings_closeRemoveDialog' });
  yield put(openSnackbar('Integration and its generated catalog data were removed.'));
  yield call(loadIntegrations);
}

export function* loadProductFilesRuns() {
  const integrations = yield select(state => state.generativeAi.integrationSettings.integrations);
  const result = yield call(generativeAiApis.getProductFilesStatus, { integrations });
  if (!result || result.error || !result.response) return null;
  yield put({
    type: 'genAi_integrationSettings_productFilesLoad_SUCCESS',
    payload: result.response,
  });
  const companyId = yield select(state => R.path(['account', 'company', 'companyId'], state));
  if (companyId) {
    for (const location of result.response.locations || []) {
      const identity = { companyId, integrationId: location.integrationId, hint: location.hint };
      const saved = readTourplanRefreshSession(identity);
      if (saved && saved.status === 'failed'
        && Date.parse(location.lastTourplanSyncAt) > saved.startedAt) {
        saveTourplanRefreshSession(identity, 'complete');
        yield put({
          type: 'genAi_integrationSettings_tourplanRefreshUpdate',
          payload: { ...identity, status: 'complete', error: '' },
        });
      }
    }
  }
  return result.response;
}

export function* watchSnapshotAfterForceRefresh({ integrationId, hint }, generationId, cacheVersion) {
  const deadline = Date.now() + 10 * 60 * 1000;
  while (Date.now() < deadline) {
    const result = yield call(loadProductFilesRuns);
    const location = ((result && result.locations) || []).find(item => (
      item.integrationId === integrationId && item.hint === hint
    ));
    if (location && (location.sourceGenerationId === generationId
      || (Number.isSafeInteger(cacheVersion) && Number.isSafeInteger(location.cacheVersion)
        && location.cacheVersion > cacheVersion))) return true;
    yield delay(5000);
  }
  return false;
}

export function* watchConcurrentTourplanRefresh({ integrationId, hint }, startedAt) {
  const deadline = startedAt + 10 * 60 * 1000;
  while (Date.now() < deadline) {
    const result = yield call(loadProductFilesRuns);
    const location = ((result && result.locations) || []).find(item => (
      item.integrationId === integrationId && item.hint === hint
    ));
    if (location && Date.parse(location.lastTourplanSyncAt) >= startedAt) return true;
    yield delay(5000);
  }
  return false;
}

const tourplanJobError = job => {
  const result = job && job.result && job.result.result;
  const error = (job && job.result && job.result.syncMetadataError)
    || (result && (result.message || (result.error && result.error.message) || result.error));
  return typeof error === 'string' && error.trim()
    ? error : 'TourPlan product refresh failed. Check the integration session and retry.';
};

const tourplanRefreshSessionKey = ({ companyId, integrationId, hint }) => (
  `tourplan-product-refresh:${companyId}:${integrationId}:${hint}`
);

const readTourplanRefreshSession = identity => {
  const raw = sessionStorage.getItem(tourplanRefreshSessionKey(identity));
  if (!raw) return null;
  try {
    const record = JSON.parse(raw);
    return record && typeof record.jobId === 'string'
      && Number.isFinite(record.startedAt)
      && ['running', 'failed'].includes(record.status)
      ? record : null;
  } catch (error) {
    return null;
  }
};

const saveTourplanRefreshSession = (identity, status, error = '') => {
  const key = tourplanRefreshSessionKey(identity);
  if (status === 'complete') {
    sessionStorage.removeItem(key);
    return;
  }
  const previous = readTourplanRefreshSession(identity);
  sessionStorage.setItem(key, JSON.stringify({
    jobId: identity.jobId, status, error,
    startedAt: previous && previous.jobId === identity.jobId
      ? previous.startedAt : Date.now(),
  }));
};

const activeTourplanRefreshPolls = new Set();

export function* watchTourplanRefreshJob({ integrationId, hint, companyId, ti2token, jobId }) {
  const identity = { integrationId, hint, companyId, jobId };
  const pollKey = tourplanRefreshSessionKey(identity) + `:${jobId}`;
  if (activeTourplanRefreshPolls.has(pollKey)) return;
  activeTourplanRefreshPolls.add(pollKey);
  const update = (status, error = '') => ({
    type: 'genAi_integrationSettings_tourplanRefreshUpdate',
    payload: { integrationId, hint, jobId, status, error },
  });
  try {
    const saved = readTourplanRefreshSession(identity);
    const startedAt = (saved && saved.startedAt) || Date.now();
    saveTourplanRefreshSession(identity, 'running');
    yield put(update('running'));
    const deadline = (saved && saved.jobId === jobId ? saved.startedAt : startedAt)
      + 60 * 60 * 1000;
    while (Date.now() < deadline) {
      const response = yield call(ti2Apis.getProductRefreshJob, {
        integrationId, companyId, ti2token, jobId,
      });
      const job = response && response.response;
      if (job && job.status === 'success') {
        if (job.result && job.result.success === true && job.result.result
          && job.result.result.catalogRefreshOutcome === 'refresh_in_progress_cache_served') {
          yield put(openSnackbar('A TourPlan product refresh is already in progress.', 'info'));
          const completed = yield call(watchConcurrentTourplanRefresh,
            { integrationId, hint }, startedAt);
          if (completed) {
            saveTourplanRefreshSession(identity, 'complete');
            yield put(update('complete'));
          } else {
            const error = 'The existing TourPlan product refresh did not complete. Check the integration and retry.';
            saveTourplanRefreshSession(identity, 'failed', error);
            yield put(update('failed', error));
            yield put(openSnackbar(error, 'error'));
          }
          return;
        }
        if (!job.result || job.result.success !== true
          || !job.result.result || job.result.result.cacheUpdated !== true
          || !job.result.syncMetadataGenerationId) {
          const error = tourplanJobError(job);
          saveTourplanRefreshSession(identity, 'failed', error);
          yield put(update('failed', error));
          yield put(openSnackbar(error, 'error'));
          return;
        }
        const recorded = yield call(watchSnapshotAfterForceRefresh, { integrationId, hint },
          job.result.syncMetadataGenerationId, job.result.result.cacheVersion);
        if (recorded) {
          saveTourplanRefreshSession(identity, 'complete');
          yield put(update('complete'));
        } else {
          const error = 'TourPlan sync record did not start. Check the product refresh and retry.';
          saveTourplanRefreshSession(identity, 'failed', error);
          yield put(update('failed', error));
          yield put(openSnackbar(error, 'error'));
        }
        return;
      }
      if (job && job.status === 'failed') {
        const error = tourplanJobError(job);
        saveTourplanRefreshSession(identity, 'failed', error);
        yield put(update('failed', error));
        yield put(openSnackbar(error, 'error'));
        return;
      }
      yield delay(5000);
    }
    const error = 'TourPlan product refresh status timed out. Check the integration and retry.';
    saveTourplanRefreshSession(identity, 'failed', error);
    yield put(update('failed', error));
    yield put(openSnackbar(error, 'error'));
  } finally {
    activeTourplanRefreshPolls.delete(pollKey);
  }
}

export function* restoreTourplanRefreshSessions(companyId, ti2token, integrations) {
  for (const integration of integrations) {
    if (integration.integrationId !== 'tourplannx' || !integration.hint) continue;
    const identity = { companyId, integrationId: integration.integrationId, hint: integration.hint };
    const saved = readTourplanRefreshSession(identity);
    if (!saved) continue;
    yield put({
      type: 'genAi_integrationSettings_tourplanRefreshUpdate',
      payload: { integrationId: integration.integrationId, hint: integration.hint,
        jobId: saved.jobId, status: saved.status, error: saved.error },
    });
    if (saved.status === 'running') {
      yield spawn(watchTourplanRefreshJob, { ...identity, ti2token, jobId: saved.jobId });
    }
  }
}

const activeForceRefreshes = new Set();

export function* forceRefreshProducts({ payload }) {
  const { companyId, ti2token } = yield call(getIntegrationContext);
  if (!companyId || !ti2token) return;
  const identity = { companyId, integrationId: payload.integrationId, hint: payload.hint };
  const refreshKey = tourplanRefreshSessionKey(identity);
  const saved = payload.integrationId === 'tourplannx'
    ? readTourplanRefreshSession(identity) : null;
  if (activeForceRefreshes.has(refreshKey) || (saved && saved.status === 'running')) {
    yield put(openSnackbar(
      payload.integrationId === 'tourplannx'
        ? 'A TourPlan product refresh is already in progress.'
        : 'A product refresh is already in progress.',
      'info',
    ));
    return;
  }
  activeForceRefreshes.add(refreshKey);
  try {
    const result = yield* apiSaga(
      'genAi_integrationSettings_forceRefresh',
      ti2Apis.forceRefreshProducts,
      [{ ...payload, companyId, ti2token }, skipLoader]
    );
    if (result) {
      yield put(openSnackbar('Product refresh requested. It may take up to four minutes.', 'info'));
      if (payload.integrationId === 'tourplannx') {
        if (result.jobId) {
          yield call(watchTourplanRefreshJob, {
            ...payload, companyId, ti2token, jobId: result.jobId,
          });
        } else {
          const error = 'TI2 did not return a TourPlan product refresh job.';
          yield put({
            type: 'genAi_integrationSettings_tourplanRefreshUpdate',
            payload: { ...payload, status: 'failed', error },
          });
          yield put(openSnackbar(error, 'error'));
        }
      }
    }
  } finally {
    activeForceRefreshes.delete(refreshKey);
  }
}

export function* reportIntegrationError(action) {
  const message = R.pathOr(
    R.pathOr(
      R.pathOr(
        R.pathOr('Unable to complete the integration request.', ['payload', 'message'], action),
        ['error', 'json', 'error'],
        action
      ),
      ['error', 'json', 'message'],
      action
    ),
    ['error', 'json', 'error', 'message'],
    action
  );
  yield put(openSnackbar(message, 'error'));
}

const integrationSettingsSagas = [
  takeLatest('genAi_integrationSettings_load', loadIntegrations),
  takeLatest('genAi_integrationSettings_productFilesRefreshRequest', loadProductFilesRuns),
  takeLatest('genAi_integrationSettings_loadData', loadIntegrationData),
  takeLatest('genAi_integrationSettings_saveRequest', saveIntegration),
  takeLatest('genAi_integrationSettings_saveCompanySettingsRequest', saveCompanySettings),
  takeLatest('genAi_integrationSettings_removeRequest', removeIntegration),
  takeEvery('genAi_integrationSettings_validateRequest', validateIntegration),
  takeEvery('genAi_integrationSettings_forceRefreshRequest', forceRefreshProducts),
  takeLatest('genAi_productSync_enterInstance', enterProductSyncInstance),
  takeLatest('genAi_productSync_pollRun', handleProductSyncPolling),
  takeLeading('genAi_productSync_startRunRequest', startProductSyncRun),
  takeLeading('genAi_productSync_autoSyncRequest', updateProductSyncAutoSync),
  takeLatest('genAi_productSync_inspectionRequest', inspectProductSyncChroma),
  takeEvery([
    'genAi_integrationSettings_createPartner_ERROR',
    'genAi_integrationSettings_createAffiliates_ERROR',
    'genAi_integrationSettings_loadCompanySettings_ERROR',
    'genAi_integrationSettings_loadToken_ERROR',
    'genAi_integrationSettings_saveCompanySettings_ERROR',
    'genAi_integrationSettings_save_ERROR',
    'genAi_integrationSettings_remove_ERROR',
    'genAi_integrationSettings_validate_ERROR',
    'genAi_integrationSettings_forceRefresh_ERROR',
    'genAi_productSync_startRun_ERROR',
    'genAi_productSync_autoSync_ERROR',
    'genAi_productSync_inspection_ERROR',
  ], reportIntegrationError),
];

export default integrationSettingsSagas;
